import React from 'react';
import { Alert, Box, Card, CardContent, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  AreaChart,
  Area,
} from 'recharts';

const COLORS = ['#1565c0', '#2e7d32', '#ed6c02', '#d32f2f', '#6a1b9a', '#00838f', '#5d4037'];

export const KpiCard: React.FC<{ label: string; value: string | number; hint?: string }> = ({
  label,
  value,
  hint,
}) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={700} sx={{ my: 0.5 }}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </CardContent>
  </Card>
);

export const ChartCard: React.FC<{
  title: string;
  height?: number;
  empty?: boolean;
  emptyText?: string;
  children: React.ReactNode;
}> = ({ title, height = 280, empty, emptyText = '표시할 데이터 없음', children }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      {empty ? (
        <Alert severity="info">{emptyText}</Alert>
      ) : (
        <Box sx={{ width: '100%', height, minHeight: height }}>{children}</Box>
      )}
    </CardContent>
  </Card>
);

/** ResponsiveContainer는 LineChart/BarChart 등 차트 루트에만 width/height를 주입한다. */
function ChartFrame({ children }: { children: React.ReactElement }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      {children}
    </ResponsiveContainer>
  );
}

export function dictToPie(data: Record<string, number | string> | null | undefined) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  return Object.entries(data)
    .map(([name, value]) => ({
      name: String(name),
      value: Number(value) || 0,
    }))
    .filter((d) => d.value > 0);
}

export const SimplePie: React.FC<{ data: { name: string; value: number }[] }> = ({ data }) => {
  if (!data?.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          데이터 없음
        </Typography>
      </Box>
    );
  }
  return (
    <ChartFrame>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" outerRadius={90} label>
          {data.map((_, i) => (
            <Cell key={i} fill={COLORS[i % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ChartFrame>
  );
};

export const SimpleBar: React.FC<{
  data: any[];
  xKey: string;
  yKey: string;
  color?: string;
}> = ({ data, xKey, yKey, color = COLORS[0] }) => {
  if (!data?.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          데이터 없음
        </Typography>
      </Box>
    );
  }
  return (
    <ChartFrame>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Bar dataKey={yKey} fill={color} />
      </BarChart>
    </ChartFrame>
  );
};

export const SimpleLine: React.FC<{
  data: any[];
  xKey: string;
  yKey: string;
  color?: string;
}> = ({ data, xKey, yKey, color = COLORS[0] }) => {
  if (!data?.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          데이터 없음
        </Typography>
      </Box>
    );
  }
  return (
    <ChartFrame>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tick={{ fontSize: 10 }} />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Line type="monotone" dataKey={yKey} stroke={color} dot strokeWidth={2} />
      </LineChart>
    </ChartFrame>
  );
};

export const SimpleArea: React.FC<{
  data: any[];
  xKey: string;
  yKey: string;
  color?: string;
}> = ({ data, xKey, yKey, color = COLORS[2] }) => {
  if (!data?.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          데이터 없음
        </Typography>
      </Box>
    );
  }
  return (
    <ChartFrame>
      <AreaChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xKey} tick={{ fontSize: 10 }} />
        <YAxis allowDecimals={false} />
        <Tooltip />
        <Area type="monotone" dataKey={yKey} stroke={color} fill={color} fillOpacity={0.25} />
      </AreaChart>
    </ChartFrame>
  );
};

export const BusyHeatmap: React.FC<{
  labels: string[];
  grid: number[][];
}> = ({ labels, grid }) => {
  const safeGrid =
    Array.isArray(grid) && grid.length
      ? grid
      : Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0));
  const flat = safeGrid.flat();
  const max = Math.max(1, ...(flat.length ? flat : [0]));
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `56px repeat(24, 18px)`,
          gap: '2px',
          minWidth: 56 + 24 * 20,
        }}
      >
        <Box />
        {Array.from({ length: 24 }, (_, h) => (
          <Typography key={h} variant="caption" align="center" sx={{ fontSize: 9 }}>
            {h}
          </Typography>
        ))}
        {safeGrid.map((row, di) => (
          <React.Fragment key={di}>
            <Typography variant="caption" sx={{ lineHeight: '18px' }}>
              {labels[di] || di}
            </Typography>
            {(row || []).map((v, hi) => {
              const intensity = (Number(v) || 0) / max;
              return (
                <Box
                  key={hi}
                  title={`${labels[di]} ${hi}시: ${v}`}
                  sx={{
                    width: 18,
                    height: 18,
                    bgcolor: `rgba(21, 101, 192, ${0.08 + intensity * 0.92})`,
                    borderRadius: 0.5,
                  }}
                />
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export function formatDurationSeconds(sec: number | null | undefined): string {
  if (sec == null || Number.isNaN(Number(sec))) return '-';
  const s = Math.max(0, Number(sec));
  if (s < 60) return `${s.toFixed(1)}초`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  if (m < 60) return r ? `${m}분 ${r}초` : `${m}분`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${h}시간 ${rm}분` : `${h}시간`;
}

export { COLORS };
